import { type WidgetConfig } from './config'
import { WidgetStage } from './rendering/stage'
import { AvatarRenderer, AVATAR_HEIGHT } from './rendering/avatarRenderer'
import { SpeechBubble } from './rendering/speechBubble'
import { fetchWidgetSession, type WidgetSessionResponse } from './api/backend'
import { RealtimeSession, type RealtimeTool } from './engine/realtimeSession'
import { AudioForwarder } from './engine/audioForwarder'
import { AnamSession } from './engine/anamSession'
import { DomAnalyzer } from './engine/domAnalyzer'
import { ToolHandler } from './engine/toolHandler'
import { PageActions } from './actions/pageActions'
import { OverlayActions } from './actions/overlayActions'
import { HighlightRenderer } from './rendering/highlightRenderer'
import { ConfirmationPrompt } from './rendering/confirmationPrompt'
import { OverlayRenderer } from './rendering/overlayRenderer'
import { WidgetControls } from './ui/controls'
import { ConversationLogger } from './api/logger'
import { RRWebRecorder } from './vision/rrwebRecorder'
import { VisionWSClient } from './vision/wsClient'
import { ActionExecutor } from './vision/actionExecutor'
import { ChatPill } from './rendering/chatPill'

type WidgetState = 'idle' | 'teasing' | 'connecting' | 'active' | 'minimized' | 'destroyed'
type AvatarState = 'idle' | 'listening' | 'thinking' | 'speaking'

/** Gap between the top of the avatar and the bottom of the speech bubble tail. */
const BUBBLE_GAP = 8
/** Delay after slide-in completes before the speech bubble appears. */
const BUBBLE_DELAY_MS = 800
/** Max session duration (10 minutes). */
const MAX_DURATION_MS = 10 * 60 * 1000
/** Warning before max duration (9 minutes). */
const MAX_DURATION_WARN_MS = 9 * 60 * 1000
/** Inactivity threshold before prompting (60 seconds). */
const INACTIVITY_PROMPT_MS = 60 * 1000
/** Inactivity threshold before auto-close (90 seconds total). */
const INACTIVITY_CLOSE_MS = 90 * 1000

/** Tool definitions sent to the Realtime API. */
const WIDGET_TOOLS: RealtimeTool[] = [
  {
    type: 'function',
    name: 'page_action',
    description:
      'Interact with the webpage the visitor is viewing. You can scroll to elements, highlight elements, click buttons/links, fill form fields, or read the page content. Describe what you want to do in natural language. Call this to understand the page, guide the visitor, or take actions on their behalf.',
    parameters: {
      type: 'object',
      properties: {
        instruction: {
          type: 'string',
          description:
            "What to do on the page, e.g. 'scroll to pricing section', 'highlight the sign-up button', 'fill the email field with test@example.com', 'read the page to understand what it contains'",
        },
      },
      required: ['instruction'],
    },
  },
  {
    type: 'function',
    name: 'show_text_card',
    description: 'Display a floating text card overlay near the avatar with additional information for the visitor. Use for key points, explanations, or summaries that benefit from visual display.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Card title' },
        body: { type: 'string', description: 'Card body text' },
      },
      required: ['title', 'body'],
    },
  },
  {
    type: 'function',
    name: 'show_image_card',
    description: 'Display a floating image card overlay with an optional caption.',
    parameters: {
      type: 'object',
      properties: {
        image_url: { type: 'string', description: 'URL of the image to display' },
        caption: { type: 'string', description: 'Optional caption below the image' },
      },
      required: ['image_url'],
    },
  },
  {
    type: 'function',
    name: 'show_bullet_list',
    description: 'Display a styled list of key points as a floating overlay card.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'List title' },
        items: { type: 'array', items: { type: 'string' }, description: 'List items' },
      },
      required: ['title', 'items'],
    },
  },
  {
    type: 'function',
    name: 'dismiss_overlays',
    description: 'Dismiss all currently visible overlay cards.',
    parameters: { type: 'object', properties: {}, required: [] },
  },
]

export class HolostaffWidget {
  private config: WidgetConfig
  private stage: WidgetStage | null = null
  private avatar: AvatarRenderer | null = null
  private bubble: SpeechBubble | null = null
  private state: WidgetState = 'idle'
  private avatarState: AvatarState = 'idle'
  private scrollHandler: (() => void) | null = null
  private idleVideoUrl: string | null = null
  private idleImageUrl: string | null = null
  private bubbleTimer: ReturnType<typeof setTimeout> | null = null

  // Conversation session
  private realtimeSession: RealtimeSession | null = null
  private audioForwarder: AudioForwarder | null = null
  private anamSession: AnamSession | null = null
  private sessionResponse: WidgetSessionResponse | null = null

  // Page actions (Issue 9) + overlays (Issue 10)
  private domAnalyzer: DomAnalyzer | null = null
  private toolHandler: ToolHandler | null = null
  private highlightRenderer: HighlightRenderer | null = null
  private confirmationPrompt: ConfirmationPrompt | null = null
  private overlayRenderer: OverlayRenderer | null = null

  // Controls + timers (Issue 11)
  private controls: WidgetControls | null = null
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null
  private maxDurationWarnTimer: ReturnType<typeof setTimeout> | null = null
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null
  private inactivityCloseTimer: ReturnType<typeof setTimeout> | null = null
  private sessionStartTime = 0

  // Conversation logging (Issue 12)
  private logger: ConversationLogger | null = null

  // Vision loop (v3)
  private rrwebRecorder: RRWebRecorder | null = null
  private visionWs: VisionWSClient | null = null
  private actionExecutor: ActionExecutor | null = null
  private visionContext: string | null = null
  private chatPill: ChatPill | null = null
  private staffDisplayName = 'Assistant'
  private staffAvatarUrl: string | null = null
  private hasHadConversation = false
  private visitorId: string

  private widgetPrompts: string[] | null

  constructor(config: WidgetConfig, widgetPrompts?: string[] | null) {
    this.config = config
    this.widgetPrompts = widgetPrompts ?? null
    this.visitorId = this.getOrCreateVisitorId()
  }

  async init(): Promise<void> {
    this.stage = new WidgetStage()
    await this.stage.init()

    this.avatar = new AvatarRenderer(this.stage.stageWidth, this.stage.stageHeight)
    this.stage.app.stage.addChild(this.avatar.container)

    await this.loadIdleAsset()

    this.bubble = new SpeechBubble(this.stage.app.ticker, this.widgetPrompts ?? undefined)
    this.bubble.container.x = 0
    this.bubble.container.y = -AVATAR_HEIGHT - BUBBLE_GAP
    this.avatar.container.addChild(this.bubble.container)

    this.avatar.setInteractive(() => this.onAvatarClick())
    this.bubble.onClick(() => this.onAvatarClick())

    this.setupTrigger()
    this.startVisionLoop()

    console.log('[HolostaffWidget] Initialized', {
      staffId: this.config.staffId,
      trigger: this.config.trigger,
      visionEnabled: !!this.config.visionWsUrl,
    })
  }

  private async loadIdleAsset(): Promise<void> {
    if (!this.avatar || !this.stage) return
    if (this.idleVideoUrl) {
      try {
        const root = this.stage.rootElement
        if (!root) throw new Error('Widget root not available')
        await this.avatar.loadIdleVideo(this.idleVideoUrl, root)
        return
      } catch {
        console.warn('[HolostaffWidget] Failed to load idle video, trying image fallback')
      }
    }
    if (this.idleImageUrl) {
      await this.avatar.loadIdleImage(this.idleImageUrl)
    } else {
      console.warn('[HolostaffWidget] No idle video or image configured')
    }
  }

  setIdleVideo(url: string): void {
    this.idleVideoUrl = url
  }

  setIdleImage(url: string): void {
    this.idleImageUrl = url
  }

  /** Set staff display info for the chat-again pill. */
  setStaffInfo(name: string, avatarUrl: string | null): void {
    this.staffDisplayName = name
    this.staffAvatarUrl = avatarUrl
  }

  // -----------------------------------------------------------------------
  // Trigger & Teaser
  // -----------------------------------------------------------------------

  private setupTrigger(): void {
    // In v3 (vision loop enabled), show the avatar immediately but without
    // speech bubble prompts — the VLM will craft contextual messages.
    if (this.config.visionWsUrl) {
      this.showAvatarOnly()
      return
    }

    switch (this.config.trigger) {
      case 'scroll':
        this.setupScrollTrigger()
        break
      case 'delay':
        setTimeout(() => this.showTeaser(), this.config.delayMs)
        break
      case 'immediate':
        this.showTeaser()
        break
    }
  }

  private setupScrollTrigger(): void {
    this.scrollHandler = () => {
      const scrollY = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPct = docHeight > 0 ? scrollY / docHeight : 0

      if (scrollPct >= this.config.scrollThreshold && this.state === 'idle') {
        this.showTeaser()
      } else if (scrollPct < this.config.scrollThreshold * 0.5 && this.state === 'teasing') {
        this.hideTeaser()
      }
    }
    window.addEventListener('scroll', this.scrollHandler, { passive: true })
  }

  /** v3: Show avatar immediately without speech bubble prompts. */
  private showAvatarOnly(): void {
    if (this.state !== 'idle' || !this.avatar || !this.stage) return
    this.state = 'teasing'
    this.avatar.slideIn(this.stage.app.ticker)
    // No speech bubble — VLM will send contextual messages via suggest_intervention
  }

  private showTeaser(): void {
    if (this.state !== 'idle' || !this.avatar || !this.stage) return
    this.state = 'teasing'
    this.avatar.slideIn(this.stage.app.ticker)
    this.bubbleTimer = setTimeout(() => this.bubble?.start(), BUBBLE_DELAY_MS)
  }

  private hideTeaser(): void {
    if (this.state !== 'teasing' || !this.avatar || !this.stage) return
    this.state = 'idle'
    if (this.bubbleTimer) { clearTimeout(this.bubbleTimer); this.bubbleTimer = null }
    this.bubble?.stop()
    this.avatar.slideOut(this.stage.app.ticker)
  }

  // -----------------------------------------------------------------------
  // Conversation lifecycle (Issues 5-8)
  // -----------------------------------------------------------------------

  private async onAvatarClick(): Promise<void> {
    // Allow click from teasing state (v2 scroll trigger) or idle state (v3 accepted suggestion)
    if (this.state !== 'teasing' && this.state !== 'idle') return

    this.state = 'connecting'
    this.hideChatPill()
    this.bubble?.stop()
    this.bubble?.hideStatic()
    // Freeze the idle video animation
    if (this.avatar) {
      const idleVid = document.querySelector('#holostaff-widget-root video') as HTMLVideoElement
      if (idleVid) idleVid.pause()
    }
    // Show connecting feedback
    this.bubble?.showStatic('Connecting...')
    console.log('[HolostaffWidget] Starting conversation...')

    try {
      // 1. Fetch session tokens
      this.sessionResponse = await fetchWidgetSession(
        this.config.staffId,
        this.config.backendUrl,
      )
      console.log('[HolostaffWidget] Session tokens received')

      // 1b. Create conversation logger
      this.logger = new ConversationLogger({
        staffId: this.config.staffId,
        staffName: this.sessionResponse.staff.name,
        backendUrl: this.config.backendUrl,
      })

      // 2. Connect Anam first (so avatar video is ready when AI speaks)
      await this.connectAnam()

      // 3. Connect Realtime API
      await this.connectRealtimeApi()

      this.state = 'active'
      this.setAvatarState('listening')
      this.sessionStartTime = Date.now()
      this.bubble?.hideStatic() // Hide "Connecting..."

      // 5. Show controls (minimize + close)
      this.showControls()

      // 6. Start session timers
      this.startSessionTimers()

      // 7. Pause vision loop during conversation
      this.visionWs?.sendConversationStarted()
      this.rrwebRecorder?.pause()

      console.log('[HolostaffWidget] Conversation active')

    } catch (err) {
      console.error('[HolostaffWidget] Failed to start conversation:', err)
      this.bubble?.hideStatic()
      this.endConversation()
    }
  }

  // -----------------------------------------------------------------------
  // Anam connection (avatar rendering, BYO audio mode)
  // -----------------------------------------------------------------------

  private async connectAnam(): Promise<void> {
    if (!this.sessionResponse) return

    this.anamSession = new AnamSession({
      sessionToken: this.sessionResponse.anamSessionToken,
    })

    this.anamSession.onConnected = () => {
      console.log('[HolostaffWidget] Anam connected')
    }

    this.anamSession.onVideoReady = (videoEl) => {
      console.log('[HolostaffWidget] Anam avatar video ready')
      // Video appearing IS the connection signal — hide "Connecting..."
      this.bubble?.hideStatic()

      if (this.avatar && this.stage) {
        this.avatar.setLiveVideo(videoEl, this.stage.app.ticker)
      }
    }

    this.anamSession.onTalkStreamInterrupted = () => {
      console.log('[HolostaffWidget] Anam talk stream interrupted')
    }

    this.anamSession.onDisconnected = (reason) => {
      console.log('[HolostaffWidget] Anam disconnected:', reason)
      this.endConversation()
    }

    this.anamSession.onError = (err) => {
      console.error('[HolostaffWidget] Anam error:', err.message)
    }

    await this.anamSession.connect()
    console.log('[HolostaffWidget] Anam session ready')
  }

  // -----------------------------------------------------------------------
  // Realtime API connection + conversation orchestration (Issues 5, 6, 8)
  // -----------------------------------------------------------------------

  private async connectRealtimeApi(): Promise<void> {
    if (!this.sessionResponse) return

    // Append page interaction instructions + vision context
    let instructions = (this.sessionResponse.staff.instructions ?? '') +
      '\n\n## Page Interaction\n' +
      'You can interact with the webpage the visitor is viewing by calling the page_action tool.\n' +
      'Call it with instruction "read the page content" first to understand what is on the page.\n' +
      'Then you can scroll to elements, highlight them, click links/buttons, or fill form fields.\n' +
      'All actions except form filling require the visitor to confirm before executing.'

    // Inject vision context if the conversation was triggered by an accepted suggestion
    if (this.visionContext) {
      instructions += '\n\n## Context from observing the user\'s session\n' + this.visionContext
      this.visionContext = null // consume it
    }

    this.realtimeSession = new RealtimeSession({
      ephemeralKey: this.sessionResponse.realtimeEphemeralKey,
      webrtcUrl: this.sessionResponse.realtimeWebrtcUrl,
      voice: this.sessionResponse.staff.voiceId,
      instructions,
      tools: WIDGET_TOOLS,
    })

    // Set up page action handlers
    this.domAnalyzer = new DomAnalyzer()
    this.highlightRenderer = new HighlightRenderer()
    this.confirmationPrompt = new ConfirmationPrompt()

    this.realtimeSession.onConnected = () => {
      console.log('[HolostaffWidget] Realtime API connected')
      // Inject opening text as the avatar's first message
      const openingText = this.sessionResponse?.staff.openingText
      if (openingText?.trim()) {
        this.realtimeSession?.sendJSON({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: '(conversation started — greet the visitor now)' }],
          },
        })
        this.realtimeSession?.sendJSON({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'assistant',
            content: [{ type: 'text', text: openingText }],
          },
        })
        this.realtimeSession?.sendJSON({ type: 'response.create' })
      }
    }

    this.realtimeSession.onDisconnected = () => {
      console.log('[HolostaffWidget] Realtime API disconnected')
      if (this.state === 'active' || this.state === 'connecting') {
        this.endConversation()
      }
    }

    // Remote audio track → AudioForwarder → Anam (lip-sync + playback)
    this.realtimeSession.onRemoteTrack = async (stream) => {
      console.log('[HolostaffWidget] Remote audio track → starting audio forwarder')
      this.audioForwarder = new AudioForwarder()

      this.audioForwarder.onAudioChunk = (pcmData) => {
        // Forward PCM audio to Anam for lip-sync and audio playback
        this.anamSession?.sendAudioChunk(pcmData)
      }

      await this.audioForwarder.start(stream)
    }

    // Create tool handler (needs realtimeSession to exist)
    this.overlayRenderer = new OverlayRenderer()
    this.toolHandler = new ToolHandler({
      realtimeSession: this.realtimeSession,
      domAnalyzer: this.domAnalyzer!,
      pageActions: new PageActions(this.domAnalyzer!, this.highlightRenderer!),
      overlayActions: new OverlayActions(this.overlayRenderer),
      confirmationPrompt: this.confirmationPrompt!,
      staffId: this.config.staffId,
      backendUrl: this.config.backendUrl,
    })

    // Log tool calls
    this.toolHandler.onToolCall = (tool, args) => {
      this.logger?.addAction(tool, args)
    }

    // Data channel message handling (Issue 8 orchestration + Issue 9 tools)
    this.realtimeSession.onDataMessage = (msg) => {
      this.handleDataChannelMessage(msg)
    }

    this.realtimeSession.onError = (err) => {
      console.error('[HolostaffWidget] Realtime error:', err.message)
      this.endConversation()
    }

    await this.realtimeSession.connect()
  }

  // -----------------------------------------------------------------------
  // Data channel message handling (Issue 8)
  // -----------------------------------------------------------------------

  private handleDataChannelMessage(msg: any): void {
    // Delegate tool call messages to the tool handler first
    if (this.toolHandler?.handleMessage(msg)) return

    const type = msg.type as string

    switch (type) {
      // --- Session lifecycle ---
      case 'session.created':
      case 'session.updated':
        console.log('[HolostaffWidget] Data:', type)
        break

      // --- VAD: user started speaking → interrupt avatar ---
      case 'input_audio_buffer.speech_started':
        console.log('[HolostaffWidget] User started speaking')
        this.anamSession?.interrupt()
        this.audioForwarder?.pause()
        this.setAvatarState('listening')
        this.resetInactivityTimer()
        break

      // --- VAD: user stopped speaking → thinking ---
      case 'input_audio_buffer.speech_stopped':
        console.log('[HolostaffWidget] User stopped speaking')
        this.setAvatarState('thinking')
        break

      // --- AI response started → resume audio forwarding ---
      case 'response.created':
        console.log('[HolostaffWidget] AI response started')
        this.audioForwarder?.resume()
        this.setAvatarState('speaking')
        break

      // --- AI response done → signal end of audio turn to Anam ---
      case 'response.done':
        console.log('[HolostaffWidget] AI response done')
        this.anamSession?.endSequence()
        this.setAvatarState('listening')
        this.resetInactivityTimer()
        // Extract AI transcript from response output for logging
        this.extractAiTranscript(msg.response)
        break

      // --- Transcription (user speech) ---
      case 'conversation.item.input_audio_transcription.completed':
        console.log('[HolostaffWidget] User said:', msg.transcript)
        this.logger?.addUserMessage(msg.transcript ?? '')
        break

      // --- Errors ---
      case 'error':
        console.error('[HolostaffWidget] Realtime error event:', msg.error)
        break

      // --- Response cancelled/failed ---
      case 'response.cancelled':
      case 'response.canceled':
      case 'response.failed':
        console.warn('[HolostaffWidget] Response ended:', type)
        this.anamSession?.endSequence()
        this.audioForwarder?.pause()
        this.setAvatarState('listening')
        break
    }
  }

  /** Extract AI-generated text from a response.done payload for logging. */
  private extractAiTranscript(response: any): void {
    if (!response?.output || !this.logger) return
    for (const item of response.output) {
      if (item.type === 'message' && item.content) {
        for (const part of item.content) {
          if (part.transcript) {
            this.logger.addStaffMessage(part.transcript)
          } else if (part.text) {
            this.logger.addStaffMessage(part.text)
          }
        }
      }
    }
  }

  // -----------------------------------------------------------------------
  // Avatar state management (Issue 8)
  // -----------------------------------------------------------------------

  private setAvatarState(newState: AvatarState): void {
    if (this.avatarState === newState) return
    const prev = this.avatarState
    this.avatarState = newState
    console.log(`[HolostaffWidget] Avatar: ${prev} → ${newState}`)

    switch (newState) {
      case 'listening':
      case 'thinking':
      case 'speaking':
      case 'idle':
        // Avatar pose is driven by Anam's native lip-sync (no explicit state commands needed)
        break
    }
  }

  // -----------------------------------------------------------------------
  // Controls — minimize, restore, close (Issue 11)
  // -----------------------------------------------------------------------

  private showControls(): void {
    this.controls = new WidgetControls()

    this.controls.onMinimize = () => {
      this.state = 'minimized'
      // Hide avatar but keep session alive
      if (this.avatar && this.stage) {
        this.avatar.slideOut(this.stage.app.ticker)
      }
      this.overlayRenderer?.dismissAll()
      console.log('[HolostaffWidget] Minimized')
    }

    this.controls.onRestore = () => {
      this.state = 'active'
      if (this.avatar && this.stage) {
        this.avatar.slideIn(this.stage.app.ticker)
      }
      console.log('[HolostaffWidget] Restored')
    }

    this.controls.onClose = () => {
      console.log('[HolostaffWidget] Close button clicked')
      this.endConversation()
    }

    this.controls.show()
  }

  // -----------------------------------------------------------------------
  // Session timers — max duration + inactivity (Issue 11)
  // -----------------------------------------------------------------------

  private startSessionTimers(): void {
    // Max duration warning at 9 minutes
    this.maxDurationWarnTimer = setTimeout(() => {
      console.log('[HolostaffWidget] Session approaching max duration')
      this.realtimeSession?.sendJSON({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: 'The session will end in about one minute. Please wrap up the conversation gracefully.' }],
        },
      })
      this.realtimeSession?.sendJSON({ type: 'response.create' })
    }, MAX_DURATION_WARN_MS)

    // Max duration hard close at 10 minutes
    this.maxDurationTimer = setTimeout(() => {
      console.log('[HolostaffWidget] Max session duration reached')
      this.endConversation()
    }, MAX_DURATION_MS)

    // Start inactivity tracking
    this.resetInactivityTimer()
  }

  /** Reset the inactivity timer (called whenever there's activity). */
  private resetInactivityTimer(): void {
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer)
    if (this.inactivityCloseTimer) clearTimeout(this.inactivityCloseTimer)

    // Prompt after 60s of silence
    this.inactivityTimer = setTimeout(() => {
      if (this.state !== 'active') return
      console.log('[HolostaffWidget] Inactivity — prompting user')
      this.realtimeSession?.sendJSON({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: 'The visitor has been silent for a while. Ask them "Still there?" in a friendly way.' }],
        },
      })
      this.realtimeSession?.sendJSON({ type: 'response.create' })

      // Auto-close after another 30s
      this.inactivityCloseTimer = setTimeout(() => {
        if (this.state !== 'active') return
        console.log('[HolostaffWidget] Inactivity — auto-closing')
        this.endConversation()
      }, INACTIVITY_CLOSE_MS - INACTIVITY_PROMPT_MS)
    }, INACTIVITY_PROMPT_MS)
  }

  private clearSessionTimers(): void {
    if (this.maxDurationTimer) { clearTimeout(this.maxDurationTimer); this.maxDurationTimer = null }
    if (this.maxDurationWarnTimer) { clearTimeout(this.maxDurationWarnTimer); this.maxDurationWarnTimer = null }
    if (this.inactivityTimer) { clearTimeout(this.inactivityTimer); this.inactivityTimer = null }
    if (this.inactivityCloseTimer) { clearTimeout(this.inactivityCloseTimer); this.inactivityCloseTimer = null }
  }

  // -----------------------------------------------------------------------
  // End conversation
  // -----------------------------------------------------------------------

  private endConversation(): void {
    // Flush conversation log (fire-and-forget)
    this.logger?.flush()
    this.logger = null

    // Clear all timers
    this.clearSessionTimers()

    // Hide controls
    this.controls?.hide()
    this.controls = null

    this.audioForwarder?.stop()
    this.audioForwarder = null

    this.anamSession?.close()
    this.anamSession = null

    this.realtimeSession?.close()
    this.realtimeSession = null

    this.sessionResponse = null
    this.avatarState = 'idle'

    // Clean up page action + overlay resources
    this.toolHandler = null
    this.domAnalyzer = null
    this.highlightRenderer?.clearAll()
    this.highlightRenderer = null
    this.confirmationPrompt?.dismiss()
    this.confirmationPrompt = null
    this.overlayRenderer?.dismissAll()
    this.overlayRenderer = null

    // Crossfade back to idle video
    if (this.avatar && this.stage) {
      this.avatar.clearLiveVideo(this.stage.app.ticker)
    }

    // Resume vision loop with conversation summary
    const conversationSummary = this.buildConversationSummary()
    this.visionWs?.sendConversationEnded(conversationSummary)
    this.rrwebRecorder?.resume()

    this.hasHadConversation = true
    this.state = 'idle'

    // After conversation, hide avatar entirely and show the minimal chat pill
    if (this.avatar) {
      this.avatar.container.visible = false
    }
    // Remove any leftover Anam video elements and wrappers
    document.querySelectorAll('[id^="anam-avatar-wrapper"]').forEach(el => el.remove())
    document.querySelectorAll('video[id^="anam-avatar-video"]').forEach(el => el.remove())
    this.showChatPill()

    console.log('[HolostaffWidget] Conversation ended')
  }

  private showChatPill(): void {
    this.chatPill?.hide()
    this.chatPill = new ChatPill()
    this.chatPill.onClick = () => {
      this.chatPill?.hide()
      this.chatPill = null
      // Start conversation directly — no avatar animation, no speech bubble
      this.onAvatarClick()
    }
    this.chatPill.show(this.staffAvatarUrl, this.staffDisplayName)
  }

  private hideChatPill(): void {
    this.chatPill?.hide()
    this.chatPill = null
  }

  // -----------------------------------------------------------------------
  // Vision loop (v3)
  // -----------------------------------------------------------------------

  private startVisionLoop(): void {
    const wsUrl = this.config.visionWsUrl
    if (!wsUrl) return // Vision disabled if no WS URL configured

    // 1. Start rrweb recording
    this.rrwebRecorder = new RRWebRecorder()

    // 2. Create WebSocket client
    this.visionWs = new VisionWSClient(wsUrl, {
      staffId: this.config.staffId,
      visitorId: this.visitorId,
      pageUrl: location.href,
      pageTitle: document.title,
    })

    // 3. Wire recorder events to WebSocket
    this.rrwebRecorder.onEvent = (event) => {
      this.visionWs?.sendRRWebEvent(event)
    }

    // 4. Create action executor (handles suggest_intervention from server)
    if (this.avatar && this.bubble && this.stage) {
      this.actionExecutor = new ActionExecutor({
        wsClient: this.visionWs,
        avatar: this.avatar,
        bubble: this.bubble,
        ticker: this.stage.app.ticker,
        getWidgetState: () => this.state,
      })

      // When user accepts a suggestion, start a conversation with vision context
      this.actionExecutor.onAccept = (context) => {
        console.log('[HolostaffWidget] Suggestion accepted — starting conversation with vision context')
        this.visionContext = context
        this.onAvatarClick()
      }
    }

    // 5. Connect and start
    this.visionWs.connect()

    // In 'required' consent mode, recording starts only after user accepts an intervention
    if (this.config.visionConsent === 'required') {
      console.log('[HolostaffWidget] Vision loop started (recording deferred — consent required)')
    } else {
      this.rrwebRecorder.start()
      console.log('[HolostaffWidget] Vision loop started', { visitorId: this.visitorId })
    }
  }

  private stopVisionLoop(): void {
    this.actionExecutor?.destroy()
    this.actionExecutor = null
    this.rrwebRecorder?.stop()
    this.rrwebRecorder = null
    this.visionWs?.close()
    this.visionWs = null
  }

  private buildConversationSummary(): string {
    return this.logger?.getSummary() ?? 'Conversation ended (no transcript available).'
  }

  private getOrCreateVisitorId(): string {
    const COOKIE_NAME = 'hs_visitor_id'
    const existing = document.cookie
      .split('; ')
      .find(c => c.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1]

    if (existing) return existing

    const id = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
    document.cookie = `${COOKIE_NAME}=${id}; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Lax`
    return id
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  destroy(): void {
    this.state = 'destroyed'
    this.endConversation()
    this.hideChatPill()
    this.stopVisionLoop()

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
    }
    if (this.bubbleTimer) {
      clearTimeout(this.bubbleTimer)
    }
    this.bubble?.destroy()
    this.avatar?.destroy()
    this.stage?.destroy()
    this.bubble = null
    this.avatar = null
    this.stage = null
  }
}
